/* ==========================================================================
   The SoArt — Postcode validation
   soart-postcode.js
   --------------------------------------------------------------------------
   A small, pure, dependency-free module: SOART.Postcode.

   It parses a UK postcode, extracts the *area* (the leading letters), and
   checks it against a set of allowed delivery areas. It performs NO DOM work
   and holds NO UI copy of its own beyond fallback defaults — the delivery
   layer owns the user-facing strings so they stay configurable.

   ------------------------------------------------------------------------
   Why area extraction needs care
   ------------------------------------------------------------------------
   A UK outward code is <area><district>, where the area is 1–2 letters and
   the district begins with a digit. Naive "starts with N" matching is wrong:

        NW1 2AB  → area "NW"   (must NOT match "N")
        N1  2AB  → area "N"
        EC1A 1BB → area "EC"   (must NOT match "E")
        E1  6AN  → area "E"
        EN1 1AA  → area "EN"   (must NOT match "E")
        SM1 1AA  → area "SM"   (Sutton — correctly REJECTED, not "S")
        SW1A 1AA → area "SW"
        WC1N 3AX → area "WC"   (must NOT match "W")

   The regex ^([A-Z]{1,2})[0-9] is greedy over the letters, so it grabs the
   full 2-letter area when one exists and stops at the first digit.

   Allowed areas for The SoArt: N, NW, E, EC, EN, W, WC, SE, SW.
   Everything else is rejected.
   ========================================================================== */

(function () {
  'use strict';

  var SOART = (window.SOART = window.SOART || {});

  if (SOART.Postcode) return; /* already defined — keep single source */

  var DEFAULT_ALLOWED_AREAS = ['N', 'NW', 'E', 'EC', 'EN', 'W', 'WC', 'SE', 'SW'];

  /* Full postcode (whitespace already removed), e.g. SW1A1AA, N11AA, EC1A1BB */
  var FULL = /^[A-Z]{1,2}[0-9][A-Z0-9]?[0-9][A-Z]{2}$/;
  /* Outward code only, e.g. SW1A, N1, EC1, W1 */
  var OUTWARD = /^[A-Z]{1,2}[0-9][A-Z0-9]?$/;
  /* Area = leading letters up to the first district digit */
  var AREA = /^([A-Z]{1,2})[0-9]/;
  /* Inward code at the end: digit + two letters */
  var INWARD = /[0-9][A-Z]{2}$/;

  var DEFAULT_MESSAGES = {
    empty: 'Please enter your delivery postcode.',
    format: 'Please enter a valid UK postcode.',
    rejected: 'Sorry, we do not deliver here.',
    accepted: 'Great! We deliver to your postcode.'
  };

  function makeResult(ok, area, outward, reason, message) {
    return {
      ok: ok,
      area: area || null,
      outward: outward || null,
      reason: reason,
      message: message
    };
  }

  var Postcode = {
    DEFAULT_ALLOWED_AREAS: DEFAULT_ALLOWED_AREAS.slice(),

    /** Uppercase and remove ALL whitespace. */
    normalize: function (value) {
      return String(value == null ? '' : value)
        .toUpperCase()
        .replace(/\s+/g, '');
    },

    /** Loose UK validity — accepts a full postcode OR an outward code only. */
    isValidFormat: function (value) {
      var pc = this.normalize(value);
      return FULL.test(pc) || OUTWARD.test(pc);
    },

    /** The postal area (leading letters), or null if it can't be parsed. */
    extractArea: function (value) {
      var pc = this.normalize(value);
      var match = pc.match(AREA);
      return match ? match[1] : null;
    },

    /** The outward code (part before the inward code), normalized. */
    outwardCode: function (value) {
      var pc = this.normalize(value);
      if (pc.length > 3 && INWARD.test(pc)) {
        return pc.slice(0, pc.length - 3);
      }
      return pc;
    },

    /** Pretty form with a single space before the inward code. */
    format: function (value) {
      var pc = this.normalize(value);
      if (pc.length > 3 && INWARD.test(pc)) {
        return pc.slice(0, pc.length - 3) + ' ' + pc.slice(pc.length - 3);
      }
      return pc;
    },

    /**
     * Validate a postcode against the allowed delivery areas.
     *
     * @param {string} value                 raw user input
     * @param {Object} [options]
     * @param {string[]} [options.allowedAreas]  defaults to DEFAULT_ALLOWED_AREAS
     * @param {Object}   [options.messages]      override copy per reason
     * @returns {{ok:boolean, area:?string, outward:?string, reason:string, message:string}}
     *          reason ∈ 'empty' | 'format' | 'area' | 'ok'
     */
    validate: function (value, options) {
      options = options || {};
      var messages = options.messages || {};
      var allowed = (options.allowedAreas && options.allowedAreas.length
        ? options.allowedAreas
        : DEFAULT_ALLOWED_AREAS
      ).map(function (area) {
        return String(area).trim().toUpperCase();
      });

      var raw = value == null ? '' : String(value).trim();

      if (!raw) {
        return makeResult(false, null, null, 'empty', messages.empty || DEFAULT_MESSAGES.empty);
      }

      if (!this.isValidFormat(raw)) {
        return makeResult(false, null, null, 'format', messages.format || DEFAULT_MESSAGES.format);
      }

      var area = this.extractArea(raw);
      var outward = this.outwardCode(raw);

      if (!area || allowed.indexOf(area) === -1) {
        return makeResult(false, area, outward, 'area', messages.rejected || DEFAULT_MESSAGES.rejected);
      }

      return makeResult(true, area, outward, 'ok', messages.accepted || DEFAULT_MESSAGES.accepted);
    }
  };

  SOART.Postcode = Postcode;

  /* --------------------------------------------------------------------------
     Reference test vectors (documented, not executed):

       validate('NW1 2AB').ok   → true   (area NW)
       validate('N1 2AB').ok    → true   (area N)
       validate('EC1A 1BB').ok  → true   (area EC)
       validate('E1 6AN').ok    → true   (area E)
       validate('SW1A 1AA').ok  → true   (area SW)
       validate('WC1N 3AX').ok  → true   (area WC)
       validate('SE1 7PB').ok   → true   (area SE)
       validate('EN1 1AA').ok   → true   (area EN)
       validate('SM1 1AA').ok   → false  (area SM — not served)
       validate('M1 1AE').ok    → false  (area M  — Manchester)
       validate('BT1 1AA').ok   → false  (area BT — Belfast)
       validate('banana').ok    → false  (reason 'format')
       validate('').ok          → false  (reason 'empty')
     -------------------------------------------------------------------------- */
})();